import { create } from "zustand";
import { persist, PersistStorage } from "zustand/middleware";

const generateGuestSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

interface AuthState {
    accessToken: string | null;
    refreshToken: string | null;
    loginTime: number | null;
    guestSessionId?: string;
    c_code?: string;
    c_company_number?: string;
    c_company?: string;
    c_director?: string; // 대표자명
    c_phone?: string;
    c_post?: string;
    c_addr1?: string;
    c_addr2?: string;
    c_tel?: string;
    c_rpost?: string;
    c_raddr1?: string;
    c_raddr2?: string;
    c_rtel?: string;
    c_sepost?: string;
    c_seaddr1?: string;
    c_seaddr2?: string;
    c_setel?: string;
    c_no_yn?: string;
    c_month_yn?: string;
    c_branch?: string;
    c_req_dept?: string;
    c_branch2?: string;
    c_rbranch?: string;
    c_sebranch?: string;
    c_bef_print?: string;
    c_ex_item1?: string;
    c_ex_item2?: string;
    c_ex_item3?: string;
    c_ex_item4?: string;
    c_ex_item5?: string;

    setAuth: (data: Partial<AuthState>) => void;
    clearAuth: () => void;
    logout: () => void;
    checkTokenExpiration: () => void;
    startExpirationTimer: () => void;
    stopExpirationTimer: () => void;
}

const customStorage: PersistStorage<AuthState> = {
    getItem: name => {
        try {
            if (typeof window === "undefined") return null;
            const item = localStorage.getItem(name);
            return item ? JSON.parse(item) : null;
        } catch {
            return null;
        }
    },
    setItem: (name, value) => {
        try {
            if (typeof window === "undefined") return;
            localStorage.setItem(name, JSON.stringify(value));
        } catch {}
    },
    removeItem: name => {
        try {
            if (typeof window === "undefined") return;
            localStorage.removeItem(name);
        } catch {}
    },
};

const TOKEN_CHECK_INTERVAL = 30 * 60 * 1000; 
const TOKEN_EXPIRATION_TIME = 12 * 60 * 60 * 1000; 

export const useMorningAuthStore = create(
    persist<AuthState>(
        (set, get) => ({
            accessToken: null,
            refreshToken: null,
            loginTime: null,
            guestSessionId:
                typeof window !== "undefined"
                    ? (() => {
                          try {
                              const existingId = localStorage.getItem("guest_session_id");
                              if (existingId) return existingId;

                              const newId = generateGuestSessionId();
                              localStorage.setItem("guest_session_id", newId);
                              return newId;
                          } catch (e) {
                              console.warn("localStorage 접근 실패:", e);
                              return undefined;
                          }
                      })()
                    : undefined,

            startExpirationTimer: () => {
                if (typeof window !== "undefined") {
                    const existingTimer = (window as any).tokenExpirationTimer;
                    if (existingTimer) {
                        clearInterval(existingTimer);
                    }

                    // 30분마다 체크
                    (window as any).tokenExpirationTimer = setInterval(() => {
                        get().checkTokenExpiration();
                    }, TOKEN_CHECK_INTERVAL);

                    // 정확한 만료 시간에 타이머 설정
                    const { loginTime } = get();
                    if (loginTime) {
                        const timeUntilExpiration = (loginTime + TOKEN_EXPIRATION_TIME) - Date.now();
                        if (timeUntilExpiration > 0) {
                            setTimeout(() => {
                                get().checkTokenExpiration();
                            }, timeUntilExpiration);
                        }
                    }
                }
            },

            stopExpirationTimer: () => {
                if (typeof window !== "undefined") {
                    const existingTimer = (window as any).tokenExpirationTimer;
                    if (existingTimer) {
                        clearInterval(existingTimer);
                        (window as any).tokenExpirationTimer = null;
                    }
                }
            },

            setAuth: data => {
                set(state => ({ 
                    ...state, 
                    ...data,
                    loginTime: data.accessToken ? Date.now() : null
                }));
                
                if (data.accessToken) {
                    get().startExpirationTimer();
                }
            },

            clearAuth: () =>
                set({
                    accessToken: null,
                    refreshToken: null,
                    guestSessionId: get().guestSessionId,
                    c_code: undefined,
                    c_company_number: undefined,
                    c_company: undefined,
                    c_director: undefined,
                    c_phone: undefined,
                    c_post: undefined,
                    c_addr1: undefined,
                    c_addr2: undefined,
                    c_tel: undefined,
                    c_rpost: undefined,
                    c_raddr1: undefined,
                    c_raddr2: undefined,
                    c_rtel: undefined,
                    c_sepost: undefined,
                    c_seaddr1: undefined,
                    c_seaddr2: undefined,
                    c_setel: undefined,
                    c_no_yn: undefined,
                    c_month_yn: undefined,
                    c_branch: undefined,
                    c_req_dept: undefined,
                    c_branch2: undefined,
                    c_rbranch: undefined,
                    c_sebranch: undefined,
                    c_bef_print: undefined,
                    c_ex_item1: undefined,
                    c_ex_item2: undefined,
                    c_ex_item3: undefined,
                    c_ex_item4: undefined,
                    c_ex_item5: undefined,
                }),

            checkTokenExpiration: () => {
                const { loginTime, logout } = get();
                if (loginTime) {
                    const now = Date.now();
                    if (now - loginTime >= TOKEN_EXPIRATION_TIME) {
                        logout();
                        window.location.href = '/login';
                    }
                }
            },

            logout: () => {
                get().stopExpirationTimer();
                set({ 
                    accessToken: null, 
                    refreshToken: null,
                    loginTime: null 
                });
            },
        }),
        {
            name: "auth-storage",
            storage: customStorage,
        },
    ),
);
